/**
 * REMOVE ACCESS WITHOUT MOVING MONEY.
 *
 * This is the operation most easily mistaken for the one next to it. A refund debits the
 * academy's Stripe balance and pays the learner back; this reaches no Stripe endpoint at all,
 * sends nothing, and pays nobody. The two controls sit one mis-click apart on the order screen,
 * so the dialog says which one this is in the first thing the operator reads, and the success
 * state ASSERTS `money_moved: false` from the answer rather than assuming it.
 *
 * The plan is shown before the operator commits, not reported afterwards: `GrantPlan` renders
 * the server's own `grant_decisions`, so what is about to go and what is about to stay are on
 * screen with the server's sentence against each row.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Banner, Button, Dialog } from '../../shared/ui';
import {
  cancelAccess,
  OPS_COPY,
  reasonProblem,
  type WireAccessResult,
  type WireOrderScreen,
} from '../api/paymentOps';
import { ReasonField } from './ReasonField';
import { GrantPlan } from './GrantPlan';
import {
  CertificateChoices,
  CertificateResults,
  certificateChoicesComplete,
  OpsFailureNotice,
  opsAlreadyHappened,
  useOperationId,
  type CertificateChoiceMap,
} from './CertificateChoices';

export function CancelAccessDialog({
  /**
   * THE WHOLE ORDER PAYLOAD, not four fields pulled out of it.
   *
   * `version`, `grant_decisions` and `certificates` have to come from ONE read. `version` is
   * optimistic concurrency on `order.updated_at` and is what stops this dialog acting on a plan
   * the server has since changed — a mismatch is refused with `stale_order_version` rather than
   * silently widening what gets revoked. A version from one read beside a plan from another
   * would defeat exactly that check, and an empty string skips it altogether. Taking the screen
   * makes the mismatch unrepresentable.
   */
  screen,
  onClose,
  onDone,
  onReload,
}: {
  screen: WireOrderScreen;
  onClose: () => void;
  /** Called when the operator dismisses the outcome. The parent owns the root query key. */
  onDone: (result: WireAccessResult) => void;
  /**
   * A fresh read of this order, offered when the SCREEN rather than the operator is what is
   * wrong — `already_done` and `stale_order_version`. Optional only because a caller that
   * cannot refetch should not be forced to pass a no-op; pass it wherever a refetch exists.
   */
  onReload?: () => void;
}) {
  const orderRef = screen.order.order_ref;
  const version = screen.version;
  const decisions = screen.grant_decisions;
  const certificates = screen.certificates;

  const [reason, setReason] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [choices, setChoices] = useState<CertificateChoiceMap>({});
  const operation = useOperationId();

  const cancel = useMutation({
    mutationFn: (operationId: string) =>
      cancelAccess(orderRef, {
        operationId,
        reason,
        version,
        certificateDecisions: choices,
      }),
  });

  const result = cancel.data;
  const reasonIsUnusable = reasonProblem(reason) !== null;
  const certificatesAnswered = certificateChoicesComplete(certificates, choices);
  const alreadyHappened = cancel.isError && opsAlreadyHappened(cancel.error);

  const submit = () => {
    setAttempted(true);
    if (operation.id === null || reasonIsUnusable || !certificatesAnswered) return;
    // THE SAME ID ON EVERY ATTEMPT. `operation.id` is minted once per mount, so a retry after a
    // failure carries the id of the first attempt and the server can recognise it as one
    // operation rather than two.
    cancel.mutate(operation.id);
  };

  return (
    <Dialog
      title="Remove access to this purchase?"
      wide
      onClose={onClose}
      footer={
        result ? (
          <Button variant="primary" onClick={() => onDone(result)}>
            Done
          </Button>
        ) : (
          <>
            {/* Hidden ONLY when the operation has already been recorded — pressing it again
                could not do anything except attempt a second write. Every other unavailable
                state keeps the button visible and disabled, with the reason under the field
                it belongs to: a button that vanishes leaves someone hunting for it. */}
            {!alreadyHappened && (
              <Button
                variant="danger"
                busy={cancel.isPending}
                disabled={operation.id === null || reasonIsUnusable || !certificatesAnswered}
                onClick={submit}
              >
                Remove access
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </>
        )
      }
    >
      {operation.problem !== null && <Banner tone="danger">{operation.problem}</Banner>}

      {result ? (
        <>
          {/* ASSERTED, NOT ASSUMED. The whole point of this operation is that no money moves,
              so the claim is made from the answer's own field. If it ever comes back true,
              something has happened that this screen was not designed to do, and saying so is
              far more useful than printing the reassurance anyway. */}
          {result.money_moved === false ? (
            <Banner tone="success">
              Access removed, and the academy confirms no money moved: nothing was sent to Stripe
              and the learner has not been paid anything back.
            </Banner>
          ) : (
            <Banner tone="danger">
              The academy reported that money moved on an operation that must not move any. Check
              this order&rsquo;s audit trail and its Stripe activity before doing anything else.
            </Banner>
          )}
          <p className="admin-muted">
            Access is now <strong>{result.access_state || 'not stated by the academy'}</strong>.
          </p>
          {/* `payment_state` is OPTIONAL on this answer and its absence is meaningful: this
              operation does not touch what was paid. A blank or an em dash here would read as
              "the payment state is unknown", which is a different and worrying claim. */}
          <p className="admin-muted">
            {result.payment_state ? (
              <>
                The payment state is <strong>{result.payment_state}</strong>.
              </>
            ) : (
              <>The payment state is untouched, because nothing was refunded.</>
            )}
          </p>
          {result.note ? <p>{result.note}</p> : null}

          <h4>What was removed</h4>
          {result.grant_decisions === undefined ? (
            <Banner tone="warning">
              The academy did not list which grants it changed. The audit trail for this operation
              records them.
            </Banner>
          ) : (
            <GrantPlan decisions={result.grant_decisions} prospective={false} />
          )}

          <h4>Certificates</h4>
          <CertificateResults results={result.certificates} />
        </>
      ) : (
        <>
          <Banner tone="warning">
            <p>
              <strong>No money moves and no refund is requested.</strong> This removes the
              learner&rsquo;s access to what they bought and nothing else: nothing is sent to
              Stripe, and they are not paid back a single sen.
            </p>
            <p>
              If they are owed their money back, close this and use the refund control instead —
              that is a different action on the same order.
            </p>
          </Banner>

          <h4>What this removes</h4>
          <GrantPlan decisions={decisions} />

          <CertificateChoices
            offers={certificates}
            value={choices}
            onChange={setChoices}
            disabled={cancel.isPending}
          />

          <ReasonField
            value={reason}
            onChange={setReason}
            showProblem={attempted}
            disabled={cancel.isPending}
          />
          <p className="admin-muted">{OPS_COPY.auditIsPermanent}</p>
        </>
      )}

      {cancel.isError && <OpsFailureNotice error={cancel.error} onReload={onReload} />}
    </Dialog>
  );
}
